import {BjThemeColors} from '@type/bj_theme';
import {BadgePill, Tooltip} from '@components/common';
import {useTranslation} from 'next-i18next';
import {applicantStatusIdObj} from '@components/templates/employer/applicant-list/functions/statusConstants';
import {ApplicantStatusEnum, StatusChangeActorEnum} from '@type/v1-api-types';
import {clsx} from 'clsx';
import { getStatusSourceLabelKey } from '@utils/resume/status-source';

const getActorDotClass = (actor?: StatusChangeActorEnum): string | undefined => {
  switch (actor) {
    case StatusChangeActorEnum.OWNER: return 'bg-primary';
    case StatusChangeActorEnum.ADMIN: return 'bg-warning';
    case StatusChangeActorEnum.AUTO: return 'bg-secondary';
    default: return undefined;
  }
};

/**
 * @description Applicant badge pill component
 * @param statusId - the status id of the applicant (number for new, interview, shortlist, etc.)
 * @param statusChangeActor - source of shortlist: 'auto' | 'admin' | 'owner'
 */
export const ApplicantBadgePill = ({
                                     statusId, statusChangeActor }: {
  statusId?: number;
  statusChangeActor?: StatusChangeActorEnum | undefined;
}) => {
  // Find the status by statusId
  const status = statusId ? applicantStatusIdObj.find((item) => item.statusId === statusId) : undefined;
  const { t } = useTranslation('common');

  // If status is not found, return null
  if (!status) return null;

  const isShortlist = statusId === (ApplicantStatusEnum as any)?.SHORTLIST;

  const actor: StatusChangeActorEnum | undefined = statusChangeActor

  const ringClasses = isShortlist
    ? (actor === StatusChangeActorEnum.AUTO
      ? 'ring-2 ring-secondary p-1 rounded-full'
      : actor === StatusChangeActorEnum.ADMIN
        ? 'ring-2 ring-warning/20 p-1 rounded-full'
        : '')
    : '';

  // Who set the status (Bestie / account manager / own team); Shortlist keeps its own wording for Bestie / account manager
  const sourceLabelKey = getStatusSourceLabelKey(actor, isShortlist);
  const sourceLabel = sourceLabelKey ? t(sourceLabelKey) : undefined;

  const actorDotClass = getActorDotClass(actor);

  const pill = (
    <span className="inline-flex items-center gap-1">
      {actorDotClass && <span className={clsx('size-2 shrink-0 rounded-full', actorDotClass)} />}
      <BadgePill
        color={status.color as BjThemeColors}
        size="xxs"
        className="max-w-24 !px-2 text-center font-medium leading-tight"
      >
        <span className="block text-wrap break-all text-pico">{t(status.label)}</span>
      </BadgePill>
    </span>
  );

  if (sourceLabel) {
    return (
      <Tooltip
        tooltipText={ sourceLabel }
        position="top"
        floating
        triggerContainerClass="inline-flex relative z-2 pointer-events-auto"
      >
        <span className={clsx('inline-flex', ringClasses)}>{pill}</span>
      </Tooltip>
    );
  }

  return <span className={clsx('inline-flex', 'pointer-events-auto relative z-2', ringClasses)}>
    {pill}
  </span>;
};
